import { useState } from 'react'
import { Checkbox, Popover } from 'antd'
import { cn } from '@/utils/cn'

/* ------------------------------------------------------------------ */
/*  列表示設定（汎用）                                                  */
/*  一覧画面の列を表示/非表示するための共通 UI。受給者検索で初出（No.30）  */
/*  だが、列定義(id→ラベル)と visibility 状態/セッターを props で受ける    */
/*  汎用設計とし、後で他画面（付款一覧・ユーザー管理等）が再利用できる。    */
/* ------------------------------------------------------------------ */

export interface ColumnVisibilityItem {
  /** 列ID（useColumnVisibility に渡す allColumnIds と一致させる）。 */
  id: string
  /** チェックボックスに表示する列ラベル。 */
  label: string
}

export interface ColumnVisibilityControlProps {
  /** 列定義（表示順）。チェックボックスはこの順で並ぶ。 */
  columns: ColumnVisibilityItem[]
  /** 列が現在表示中か。 */
  isVisible: (id: string) => boolean
  /** 列の表示/非表示を切り替える。 */
  setVisible: (id: string, on: boolean) => void
  /** すべて表示にリセットする。 */
  onReset: () => void
  /**
   * 常に表示（チェックを外せない）列ID。該当列はチェック済み・disabled で表示する。
   * useColumnVisibility に渡した alwaysVisible と揃える。
   */
  alwaysVisible?: string[]
  /** 現在非表示にしている列数（ボタンのバッジ表示用）。 */
  hiddenCount?: number
  /** 非表示にできない列（例: 並び替え中の列）。見た目は非活性だがクリックで理由を通知する。 */
  isHideDisabled?: (id: string) => boolean
  /** isHideDisabled 列をクリックしたときの理由（title / toast 用。setVisible(id, false) 側で表示）。 */
  hideDisabledTitle?: string
  /** ボタンに付与する追加クラス（既存のツールバー流儀に合わせる）。 */
  buttonClassName?: string
  /** ボタンラベル。既定は「列表示設定」。 */
  buttonLabel?: string
}

const COLUMNS_ICON = (
  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16M9 5v14M4 5v14h16V5" />
  </svg>
)

export function ColumnVisibilityControl({
  columns,
  isVisible,
  setVisible,
  onReset,
  alwaysVisible = [],
  hiddenCount = 0,
  isHideDisabled,
  hideDisabledTitle,
  buttonClassName,
  buttonLabel = '列表示設定',
}: ColumnVisibilityControlProps) {
  const [open, setOpen] = useState(false)
  const alwaysVisibleSet = new Set(alwaysVisible)

  const content = (
    <div className="w-60">
      <div className="mb-2 flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
        <span className="text-sm font-medium text-gray-700">表示する列</span>
        <button
          type="button"
          onClick={onReset}
          className="cursor-pointer text-xs font-medium text-blue-600 hover:underline"
        >
          すべて表示にリセット
        </button>
      </div>
      <div className="flex max-h-72 flex-col gap-1.5 overflow-y-auto pr-1">
        {columns.map((col) => {
          const locked = alwaysVisibleSet.has(col.id)
          const hideLocked = !locked && (isHideDisabled?.(col.id) ?? false)
          const visible = isVisible(col.id)
          // native disabled は click を落とすため、並び替え中の「外せない」列は見た目だけ非活性にし、
          // オーバーレイで setVisible(false) を届けて呼び出し側の toast を出す。
          const interceptHideClick = hideLocked && visible
          return (
            <div
              key={col.id}
              className={cn('relative w-full', interceptHideClick && 'cursor-not-allowed')}
            >
              <Checkbox
                checked={visible}
                disabled={locked || interceptHideClick}
                title={hideLocked ? hideDisabledTitle : undefined}
                onChange={(e) => setVisible(col.id, e.target.checked)}
              >
                <span className={cn('text-sm text-gray-700', hideLocked && 'text-amber-800')}>{col.label}</span>
              </Checkbox>
              {interceptHideClick ? (
                <button
                  type="button"
                  className="absolute inset-0 z-10 cursor-not-allowed border-0 bg-transparent p-0"
                  title={hideDisabledTitle}
                  aria-label={hideDisabledTitle}
                  onClick={() => setVisible(col.id, false)}
                />
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
    >
      <button
        type="button"
        className={cn(
          'inline-flex items-center shrink-0 px-5 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition shadow-sm cursor-pointer whitespace-nowrap',
          buttonClassName,
        )}
      >
        {COLUMNS_ICON}
        {buttonLabel}
        {hiddenCount > 0 && (
          <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-700">
            {hiddenCount}
          </span>
        )}
      </button>
    </Popover>
  )
}

export default ColumnVisibilityControl
